import { jsx, jsxs } from "react/jsx-runtime";
import { createContext, useContext, Fragment, useCallback, useReducer, useState, useEffect } from "react";
import { useTheme } from "../ThemeProvider/ThemeContext.mjs";
import { ThemePortal } from "../ThemePortal/ThemePortal.mjs";
import { Toaster } from "./Toaster.mjs";
let toastCounter = 0;
const ToastControllerContext = createContext(null);
const QUEUE_TOAST = 0;
const REMOVE_TOAST = 1;
function reducer(state, action) {
  switch (action.type) {
    case QUEUE_TOAST: {
      const { toast } = action;
      const hasExistingToast = state.toasts.some((t) => t.dedupeKey === toast.dedupeKey);
      if (hasExistingToast) {
        return {
          toasts: state.toasts.map((t) => {
            if (t.dedupeKey === toast.dedupeKey) {
              return {
                ...t,
                shouldRemove: true
              };
            }
            return t;
          }),
          queuedToasts: {
            ...state.queuedToasts,
            [toast.dedupeKey]: toast
          }
        };
      }
      return {
        ...state,
        toasts: [...state.toasts, action.toast]
      };
    }
    case REMOVE_TOAST: {
      const toasts = state.toasts.filter(({ dedupeKey }) => dedupeKey !== action.dedupeKey);
      const queuedToast = state.queuedToasts[action.dedupeKey];
      if (queuedToast) {
        return {
          queuedToasts: {
            ...state.queuedToasts,
            [action.dedupeKey]: void 0
          },
          toasts: [...toasts, queuedToast]
        };
      }
      return {
        ...state,
        toasts
      };
    }
  }
}
const InternalToastProvider = ({ children }) => {
  const [{ toasts }, dispatch] = useReducer(reducer, {
    toasts: [],
    queuedToasts: {}
  });
  const addToast = useCallback((toast) => dispatch({ type: QUEUE_TOAST, toast }), []);
  const removeToast = useCallback((dedupeKey) => {
    dispatch({ type: REMOVE_TOAST, dedupeKey });
  }, []);
  return /* @__PURE__ */ jsxs(ToastControllerContext.Provider, { value: { addToast, removeToast }, children: [
    children,
    /* @__PURE__ */ jsx(ToastPortal, { children: /* @__PURE__ */ jsx(Toaster, { toasts, removeToast }) })
  ] });
};
const ToastProvider = ({ children }) => {
  const currentContext = useContext(ToastControllerContext);
  if (currentContext !== null) {
    return /* @__PURE__ */ jsx(Fragment, { children });
  }
  return /* @__PURE__ */ jsx(InternalToastProvider, { children });
};
const ToastPortal = ({ children }) => {
  const [toastElement, setElement] = useState(null);
  useEffect(() => {
    const toastContainerId = "ds-toast-container";
    let element = document.getElementById(toastContainerId);
    if (!element) {
      element = document.createElement("div");
      element.setAttribute("id", toastContainerId);
      element.setAttribute("class", "");
      document.body.appendChild(element);
    }
    setElement(element);
  }, []);
  if (!toastElement) {
    return null;
  }
  return /* @__PURE__ */ jsx(ThemePortal, { container: toastElement, children });
};
const useToast = () => {
  const { vanillaTheme } = useTheme();
  const context = useContext(ToastControllerContext);
  if (context === null) {
    throw new Error('No "ToastProvider" configured');
  }
  const { addToast, removeToast } = context;
  return useCallback(
    (toast) => {
      const id = `${toastCounter++}`;
      const dedupeKey = toast.key ?? id;
      addToast({
        ...toast,
        vanillaTheme,
        id,
        dedupeKey,
        shouldRemove: false
      });
      return {
        key: dedupeKey,
        removeToast: () => {
          removeToast(dedupeKey);
        }
      };
    },
    [vanillaTheme, addToast, removeToast]
  );
};
export {
  ToastProvider,
  useToast
};
